import { useEffect } from 'react';
import {
  Monitor,
  MonitorUp,
  Eye,
  MousePointer,
  PhoneOff,
  Loader2,
  AlertCircle,
  Shield,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { cn } from '@/lib/utils';
import type { RemoteSession, ConnectionState } from '@/hooks/useRemoteSession';

interface RemoteSessionHostProps {
  session: RemoteSession | null;
  connectionState: ConnectionState;
  localStream: MediaStream | null;
  error: string | null;
  onEndSession: () => void;
  isEndingSession?: boolean;
}

/**
 * Component shown when a cashier is hosting a remote session (sharing their screen).
 * Displays connection status and allows ending the session.
 */
export function RemoteSessionHost({
  session,
  connectionState,
  localStream,
  error,
  onEndSession,
  isEndingSession = false,
}: RemoteSessionHostProps) {
  // Don't render anything if no active session
  if (!session || !['connecting', 'active'].includes(session.status)) {
    return null;
  }

  const isConnected = connectionState === 'connected';
  const isConnecting = connectionState === 'connecting';
  const isFullControl = session.session_type === 'full_control';

  return (
    <div
      className={cn(
        'fixed bottom-4 right-4 z-50 max-w-sm rounded-lg border shadow-lg bg-card',
        isConnected ? 'border-blue-500/50' : 'border-amber-500/50'
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-t-lg',
          isConnected ? 'bg-blue-500/10' : 'bg-amber-500/10'
        )}
      >
        <div
          className={cn(
            'w-10 h-10 rounded-full flex items-center justify-center',
            isConnected ? 'bg-blue-500/20' : 'bg-amber-500/20'
          )}
        >
          {isConnecting ? (
            <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
          ) : (
            <MonitorUp className={cn('h-5 w-5', isConnected ? 'text-blue-500' : 'text-amber-500')} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm">
            {isConnecting ? 'Connecting...' : 'Screen Sharing Active'}
          </h4>
          <p className="text-xs text-muted-foreground truncate">
            {isFullControl ? 'Full control enabled' : 'View only'}
          </p>
        </div>
        <Badge
          variant={isConnected ? 'default' : 'secondary'}
          className={cn(isConnected && 'bg-blue-500')}
        >
          {isConnecting ? 'Connecting' : 'Live'}
        </Badge>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-3">
        {/* Session info */}
        <div className="flex items-center gap-2 text-sm">
          <Eye className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">An administrator is viewing your screen</span>
        </div>

        {isFullControl && (
          <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
            <MousePointer className="h-4 w-4" />
            <span>They can control your mouse and keyboard</span>
          </div>
        )}

        {/* Stream status */}
        {localStream && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Monitor className="h-3.5 w-3.5" />
            <span>
              Sharing: {localStream.getVideoTracks()[0]?.label || 'Screen'}
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t bg-muted/30 rounded-b-lg">
        <Button
          variant="destructive"
          size="sm"
          className="w-full gap-2"
          onClick={onEndSession}
          disabled={isEndingSession}
        >
          {isEndingSession ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PhoneOff className="h-4 w-4" />
          )}
          End Session
        </Button>
      </div>
    </div>
  );
}

interface IncomingSessionRequestProps {
  session: RemoteSession;
  adminName?: string;
  onAccept: () => void;
  onReject: () => void;
  isAccepting?: boolean;
  isRejecting?: boolean;
  isScreenShareSupported?: boolean;
}

/**
 * Modal shown when an admin requests to connect to a cashier's session.
 */
export function IncomingSessionRequest({
  session,
  adminName = 'An administrator',
  onAccept,
  onReject,
  isAccepting = false,
  isRejecting = false,
  isScreenShareSupported = true,
}: IncomingSessionRequestProps) {
  // Play notification sound on mount
  useEffect(() => {
    // Simple notification sound using Web Audio API
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(554.37, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.2);

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch {
      // Audio not supported or blocked
    }
  }, []);

  const isFullControl = session.session_type === 'full_control';

  return (
    <ResponsiveModal open={true} onOpenChange={() => {}}>
      <ResponsiveModalContent maxWidth="sm">
        <ResponsiveModalHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
              <Monitor className="h-6 w-6 text-blue-500" />
            </div>
            <div>
              <ResponsiveModalTitle>Remote Session Request</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                {adminName} wants to view your screen
              </ResponsiveModalDescription>
            </div>
          </div>
        </ResponsiveModalHeader>

        <ResponsiveModalBody>
          <div className="space-y-4">
            {/* Requester info */}
            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{adminName}</p>
                <p className="text-sm text-muted-foreground">
                  Requesting {isFullControl ? 'full control' : 'view only'} access
                </p>
              </div>
            </div>

            {/* What this means */}
            <div className="space-y-2">
              <p className="text-sm font-medium">If you accept:</p>
              <ul className="text-sm text-muted-foreground space-y-1.5 ml-4">
                <li className="flex items-start gap-2">
                  <Eye className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>They will see everything on your screen</span>
                </li>
                {isFullControl && (
                  <li className="flex items-start gap-2 text-blue-600 dark:text-blue-400">
                    <MousePointer className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>They can control your mouse and keyboard</span>
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <Shield className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>You can end the session at any time</span>
                </li>
              </ul>
            </div>

            {/* Security note */}
            <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Only accept if you requested support or recognize the administrator.
              </p>
            </div>

            {/* Device not supported warning */}
            {!isScreenShareSupported && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 text-red-500 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-700 dark:text-red-300">
                      Screen sharing not supported
                    </p>
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      This device (iPad/mobile) doesn't support screen sharing. Please use the BlazePOS desktop app or a desktop browser to accept remote sessions.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button
            variant="outline"
            onClick={onReject}
            disabled={isAccepting || isRejecting}
          >
            {isRejecting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Decline
          </Button>
          <Button
            variant="blaze"
            onClick={onAccept}
            disabled={isAccepting || isRejecting || !isScreenShareSupported}
          >
            {isAccepting ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <MonitorUp className="h-4 w-4 mr-2" />
            )}
            {isScreenShareSupported ? 'Accept & Share Screen' : 'Not Supported'}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

